/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import { css, Global } from "@emotion/react";
import reset from "./reset";

import "@fontsource/noto-sans-jp/japanese.css";
import "@fontsource-variable/fira-code";
// import "pretendard/dist/web/static/pretendard-dynamic-subset.css";
import "pretendard/dist/web/static/pretendard.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { PropsWithChildren } from "react";
import { Maybe } from "@/frameworks/libraries";

const styles = css`
	@import url("https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&display=swap");
	${reset};

	html body {
		font-family: "Pretendard", "맑은 고딕", "MalgunGothic", sans-serif;
		font-weight: 400;
		line-height: 1.3;
	}
	html[lang="ko"] body *:not([class^="vjs-"]),
	html[lang="ko"] button:not(.fa) {
		font-family: "Pretendard", "맑은 고딕", "MalgunGothic", sans-serif;
	}
	html[lang="ko"].win-xp body *,
	html[lang="ko"].win-xp button:not(.fa) {
		font-family: "맑은 고딕", "MalgunGothic", sans-serif;
	}
	html[lang="en"] body *,
	html[lang="en"] button:not(.fa) {
		font-family: "Pretendard", "Open Sans", sans-serif;
	}
	html[lang="ja"] body *,
	html[lang="ja"] button:not(.fa) {
		font-family: "Pretendard", "Noto Sans JP", "meiryo", sans-serif;
	}

	body {
		font-size: 16px;
	}

	body * {
		box-sizing: border-box;
		outline: none;

		&::-webkit-scrollbar {
			width: 8px;
			height: 8px;
			background-color: #e6e6e6;
		}
		&::-webkit-scrollbar-track {
			-webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
		}
		&::-webkit-scrollbar-thumb {
			-webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
			border-radius: 10px;
			background-color: rgba(0, 0, 0, 0.4);
		}
	}

	button {
		padding: 0;
		background: none;
		border: none;
	}

	body {
		background-color: #d8eeff;
		background-size: cover;
		background-attachment: fixed;

		&::after {
			content: "";
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-color: rgba(255, 255, 255, 0.3);
			transition: opacity 50ms ease-in-out;
			opacity: 0;
			z-index: -1;
		}
		&.flash::after {
			opacity: 1;
			z-index: 20020;
		}
	}

	main.remove-shadow * {
		box-shadow: none !important;
	}

	#shot-flash {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(255, 255, 255, 0.3);
		transition: opacity 50ms ease-in-out;
		opacity: 0;
		z-index: -1;

		&.visible {
			opacity: 1;
			z-index: 20020;
		}
	}

	.slick-dot li {
		position: relative;
		display: inline-block;
		width: 20px;
		height: 20px;
		/*margin: 1px;*/
		padding: 0;
		cursor: pointer;
	}

	.slick-dot li button {
		font-size: 0;
		line-height: 0;
		display: block;
		width: 20px;
		height: 20px;
		padding: 5px;
		cursor: pointer;
		color: transparent;
		border: 0;
		outline: 0;
		background: 0 0;
	}

	.slick-dot li button:before {
		font-size: 2.7rem;
		line-height: 20px;
		position: absolute;
		top: 0;
		left: 0;
		width: 20px;
		height: 20px;
		content: "•";
		text-align: center;
		opacity: 0.75;
		color: #878b93;
	}

	.slick-dot li.slick-active button:before {
		opacity: 0.75;
		color: #2e2f32;
	}

	#options {
		display: none;
	}

	.video-js .vjs-big-play-button {
		display: none;
	}

	.video-js .vjs-control-bar {
		position: fixed;
	}

	.video-js video.vjs-tech {
		position: fixed;
	}

	.chat-font {
		font-family: "Pretendard", "Noto Color Emoji" !important;
	}
`;

const DocumentViewer_Global_Styles = css`
	${reset};

	html body {
		font-family: "Pretendard", "맑은 고딕", "MalgunGothic", sans-serif !important;
		font-weight: 400;
		line-height: 1.3;
	}
	html[lang="ko"] body *:not([class^="vjs-"]),
	html[lang="ko"] button:not(.fa) {
		font-family: "Pretendard", "맑은 고딕", "MalgunGothic", sans-serif !important;
	}
	html[lang="ko"].win-xp body *,
	html[lang="ko"].win-xp button:not(.fa) {
		font-family: "맑은 고딕", "MalgunGothic", sans-serif !important;
	}
	html[lang="en"] body *,
	html[lang="en"] button:not(.fa) {
		font-family: "Pretendard", "Open Sans", sans-serif !important;
	}
	html[lang="ja"] body *,
	html[lang="ja"] button:not(.fa) {
		font-family: "Pretendard", "Noto Sans JP", "meiryo", sans-serif !important;
	}

	body {
		font-size: 16px;
	}

	body * {
		box-sizing: border-box;
		outline: none;

		&::-webkit-scrollbar {
			width: 8px;
			height: 8px;
			background-color: #e6e6e6;
		}
		&::-webkit-scrollbar-track {
			-webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
		}
		&::-webkit-scrollbar-thumb {
			-webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
			border-radius: 10px;
			background-color: rgba(0, 0, 0, 0.4);
		}
	}

	#root {
		min-width: 100vw;
		min-height: 100vh;
		background-color: #2e2f32e6;
		background-size: cover;
		background-attachment: fixed;

		&::after {
			content: "";
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-color: rgba(255, 255, 255, 0.3);
			transition: opacity 50ms ease-in-out;
			opacity: 0;
			z-index: -1;
		}
	}

	main.remove-shadow * {
		box-shadow: none !important;
	}

	#options {
		display: none;
	}
`;

type GlobalStylesProps = {
	viewer_mode?: boolean;
};

export default function GlobalStyles(props: PropsWithChildren<GlobalStylesProps>) {
	return (
		<Maybe test={!props.viewer_mode}>
			<Global styles={styles} />
			{props.children}
			<Maybe.Fallback>
				<Global styles={DocumentViewer_Global_Styles} />
				{props.children}
			</Maybe.Fallback>
		</Maybe>
	);
}
